export interface BsonTypeInfo {
  name: string;
  hasArgs: boolean;
  argCount?: number;
  ejsonKey: string;
  snippet: string;
  description: string;
  validateArg?: (arg: string) => string | null;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validateObjectId(arg: string): string | null {
  return OBJECT_ID_RE.test(arg) ? null : "ObjectId requires exactly 24 hex characters";
}

function validateISODate(arg: string): string | null {
  const ts = Date.parse(arg);
  return Number.isNaN(ts) ? "ISODate requires a valid ISO 8601 date string" : null;
}

function validateUUID(arg: string): string | null {
  return UUID_RE.test(arg) ? null : "UUID must match xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx format";
}

function validateNumberLong(arg: string): string | null {
  if (!/^-?\d+$/.test(arg)) {
    return "NumberLong requires a 64-bit integer value (digits only, optional leading minus)";
  }
  return null;
}

function validateNumberInt(arg: string): string | null {
  if (!/^-?\d+$/.test(arg)) {
    return "NumberInt requires a 32-bit integer value (digits only, optional leading minus)";
  }
  const n = Number(arg);
  if (n < -2147483648 || n > 2147483647) {
    return "NumberInt value out of 32-bit signed integer range (-2147483648 to 2147483647)";
  }
  return null;
}

function validateNumberDecimal(arg: string): string | null {
  if (!/^-?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(arg) && arg !== "NaN" && arg !== "Infinity" && arg !== "-Infinity") {
    return "NumberDecimal requires a valid decimal number string";
  }
  return null;
}

function validateBinDataSubtype(arg: string): string | null {
  const n = Number(arg);
  if (!Number.isInteger(n) || n < 0 || n > 255) {
    return "BinData subtype must be an integer between 0 and 255";
  }
  return null;
}

// ---------------------------------------------------------------------------
// BSON Types
// ---------------------------------------------------------------------------

export const BSON_TYPES: BsonTypeInfo[] = [
  {
    name: "ObjectId",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$oid",
    snippet: "ObjectId('${1:000000000000000000000000}')",
    description: "12-byte unique identifier typically used as the default value for _id fields.",
    validateArg: validateObjectId,
  },
  {
    name: "ISODate",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$date",
    snippet: "ISODate('${1:2024-01-01T00:00:00.000Z}')",
    description: "Date wrapper that stores dates as ISO 8601 strings with millisecond precision.",
    validateArg: validateISODate,
  },
  {
    name: "NumberLong",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$numberLong",
    snippet: "NumberLong('${1:0}')",
    description: "64-bit signed integer (long). Wraps values that exceed the 32-bit integer range.",
    validateArg: validateNumberLong,
  },
  {
    name: "NumberInt",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$numberInt",
    snippet: "NumberInt(${1:0})",
    description: "32-bit signed integer. Forces a value to be stored as a 32-bit integer in BSON.",
    validateArg: validateNumberInt,
  },
  {
    name: "NumberDecimal",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$numberDecimal",
    snippet: "NumberDecimal('${1:0.0}')",
    description: "128-bit decimal-based floating-point (IEEE 754 Decimal128) for exact decimal representation.",
    validateArg: validateNumberDecimal,
  },
  {
    name: "UUID",
    hasArgs: true,
    argCount: 1,
    ejsonKey: "$uuid",
    snippet: "UUID('${1:00000000-0000-0000-0000-000000000000}')",
    description: "Universally unique identifier stored as a Binary subtype 4 value.",
    validateArg: validateUUID,
  },
  {
    name: "Binary",
    hasArgs: true,
    argCount: 2,
    ejsonKey: "$binary",
    snippet: "Binary(Buffer.from('${1:base64}', 'base64'), ${2:0})",
    description: "Generic binary data with a subtype byte indicating the kind of data stored.",
  },
  {
    name: "BinData",
    hasArgs: true,
    argCount: 2,
    ejsonKey: "$binary",
    snippet: "BinData(${1:0}, '${2:base64}')",
    description: "Legacy binary data constructor. First argument is subtype (0-255), second is base64-encoded data.",
    validateArg: validateBinDataSubtype,
  },
  {
    name: "Timestamp",
    hasArgs: true,
    argCount: 2,
    ejsonKey: "$timestamp",
    snippet: "Timestamp(${1:0}, ${2:0})",
    description: "Special internal type used for replication. First value is seconds since epoch, second is an ordinal increment.",
  },
  {
    name: "DBRef",
    hasArgs: true,
    argCount: 2,
    ejsonKey: "$ref",
    snippet: "DBRef('${1:collection}', ObjectId('${2:000000000000000000000000}'))",
    description: "Database reference containing a collection name and an object id (and optionally a database name).",
  },
  {
    name: "MinKey",
    hasArgs: false,
    ejsonKey: "$minKey",
    snippet: "MinKey",
    description: "Special type that compares lower than all other possible BSON element values.",
  },
  {
    name: "MaxKey",
    hasArgs: false,
    ejsonKey: "$maxKey",
    snippet: "MaxKey",
    description: "Special type that compares higher than all other possible BSON element values.",
  },
];

// ---------------------------------------------------------------------------
// Quick lookup by constructor name
// ---------------------------------------------------------------------------

export const BSON_TYPE_MAP: Map<string, BsonTypeInfo> = new Map(
  BSON_TYPES.map((t) => [t.name, t]),
);

// ---------------------------------------------------------------------------
// BSON keywords (no parentheses, used as bare values)
// ---------------------------------------------------------------------------

export const BSON_KEYWORDS: string[] = ["MinKey", "MaxKey", "Infinity", "NaN"];
